import { ApolloClient, InMemoryCache, Observable, createHttpLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { v4 as uuidv4 } from 'uuid';
import { forceLogout, refreshTokens, setRefreshClient } from './refreshTokens';

const AUTH_ROUTES = ['/login', '/register', '/forgot', '/recover', '/reset', '/configured', '/setupmfa', '/select-tenant'];
const REFRESH_OPERATION_NAME = 'RefreshTokens';

const cache = new InMemoryCache({
    addTypename: false
});

const httpLink = createHttpLink({
    uri: process.env.REACT_APP_GRAPHQL_URL,
    credentials: 'include'
});

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
    const httpStatus = networkError && 'statusCode' in networkError ? networkError.statusCode : undefined;
    const isAuthError =
        httpStatus === 401 ||
        graphQLErrors?.some((err) => err.extensions?.code === 'UNAUTHENTICATED' || err.message?.toLowerCase().includes('unauthorized'));

    if (!isAuthError) return undefined;

    const isAuthRoute = AUTH_ROUTES.some((route) => window.location.pathname.includes(route));
    if (isAuthRoute) return undefined;

    // If the refresh mutation itself failed, give up and log out.
    if (operation.operationName === REFRESH_OPERATION_NAME) {
        forceLogout();
        return undefined;
    }

    // Prevent infinite loops: only attempt refresh once per operation.
    const context = operation.getContext();
    if (context.retriedAfterRefresh) {
        forceLogout();
        return undefined;
    }

    return new Observable((observer) => {
        refreshTokens()
            .then(() => {
                operation.setContext({ ...context, retriedAfterRefresh: true });
                forward(operation).subscribe({
                    next: observer.next.bind(observer),
                    error: observer.error.bind(observer),
                    complete: observer.complete.bind(observer)
                });
            })
            .catch((err) => {
                forceLogout();
                observer.error(err);
            });
    });
});

const authLink = setContext((_, { headers }) => {
    // Generate unique nonce and timestamp for each request (no Authorization from localStorage; rely on credentials: 'include')
    const nonce = uuidv4();
    const timestamp = Date.now().toString();

    return {
        headers: {
            ...headers,
            'X-Request-Nonce': nonce,
            'X-Request-Timestamp': timestamp
        }
    };
});

export const client = new ApolloClient({
    link: errorLink.concat(authLink.concat(httpLink)),
    cache
});

setRefreshClient(client);
